import { useState, useEffect, useRef } from "react";
import "../styles/PopUp.css"
import { LuCheck, LuNotebook, LuX, LuLoaderCircle } from "react-icons/lu";
import TeamService from "../services/TeamService";
import { APIStatus } from "../constants/constant";
import { adjustTextColor, getInitials, stringToColor } from "../functions/function";
import { ImSpinner9 } from "react-icons/im";
import DiagConfirmation from "./DiagConfirmation";

const AddMemberPopUp = (props) => {
  const limitAddMembers = 200;
  const { isOpen, onClose, fetchTeamMembers, idTeamSelected } = props;
  const searchBoxRef = useRef(null);

  // const idTeamSelected = localStorage.getItem("idTeamSelected");

  const [isSearching, setIsSearching] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [isSuccessful, setIsSuccessful] = useState(false);
  const [isAdded, setIsAdded] = useState(false);

  const [mailNotExist, setMailNotExist] = useState(false);
  const [listFull, setListFull] = useState(false);
  const [content, setContent] = useState("");
  const [latestInput, setLatestInput] = useState("");
  const [numberOfEmails, setNumberOfEmails] = useState(0);

  const { getSearchUsers, postExistUsers, postAddMembers } = TeamService();
  const [resultList, setResultList] = useState([]);
  const [memberList, setMemberList] = useState([]);

  const searchUsers = async (nameormail, signal) => {
    setIsSearching(true);
    try {
      const response = await getSearchUsers(nameormail, signal);
      if (response.status === APIStatus.success) {
        setResultList(response.data)
      } else console.error(response.data);
    } catch (error) {
      console.error("Error fetching data: ", error);
    } finally {
      if (!signal.aborted) {
        setIsSearching(false);
      }
    }
  }

  const handleAddToList = async (content) => {
    setIsExecuting(true);
    let addedCount = 0;
    let emailList = content.split(";").map(email => email.trim()).filter(email => email !== "");
    setNumberOfEmails(emailList.length);

    let emailsToProcess = emailList.slice(0, limitAddMembers);
    let remainingEmails = emailList.slice(limitAddMembers);

    // const validEmails = [];
    const invalidEmails = [];

    try {
      while (emailsToProcess.length > 0 && addedCount < limitAddMembers) {
        // Gửi tất cả email cùng lúc
        const promises = emailsToProcess.map(email =>
          postExistUsers([email])
            .then(res => ({ email, res }))
            .catch(err => ({ email, error: err }))
        );

        // Chờ tất cả hoàn thành
        promises.forEach(promise => {
          promise.then(({ email, res, error }) => {
            if (error || res?.status !== APIStatus.success) {
              invalidEmails.push(email);
              // setNumberOfEmails(prev => prev - 1);
              return;
            }

            // Trường hợp API trả về [null] -> invalid
            if (Array.isArray(res.data) && res.data[0] !== null) {
              // Email hợp lệ → cập nhật ngay memberList
              setMemberList(prev => [...prev, res.data[0]]);
              addedCount++;
            } else {
              invalidEmails.push(email);
              // setNumberOfEmails(prev => prev - 1);
            }
          });
        });

        // Chờ tất cả promise hoàn tất trước khi lấy thêm email mới
        await Promise.allSettled(promises);

        // Nếu chưa đủ limitAddMembers thì lấy thêm từ remainingEmails
        const needMore = limitAddMembers - addedCount; // lấy từ state luôn
        if (needMore > 0 && remainingEmails.length > 0) {
          emailsToProcess = remainingEmails.slice(0, needMore);
          remainingEmails = remainingEmails.slice(needMore);
        } else {
          break;
        }
      }
      // Update state with valid emails
      // setMemberList([...memberList, ...validEmails]);
      // setNumberOfEmails(Math.min(addedCount, limitAddMembers));

      const newContentList = [...invalidEmails, ...remainingEmails];
      if (remainingEmails?.length > 0) {
        setListFull(true);
      }
      else if (invalidEmails?.length > 0) {
        setMailNotExist(true);
      }
      if (newContentList?.length > 0) {
        setContent(newContentList.join("; ") + ";");
      } else {
        setContent("");
        setMailNotExist(false);
        setListFull(false);
      }
    } catch (error) {
      console.error("Error posting data: ", error);
    } finally {
      setIsExecuting(false);
    }
  }

  const formatEmails = (emailString) => {
    // Extract valid emails
    const emailPattern = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
    let emails = emailString.match(emailPattern) || [];

    // Remove extracted emails from the original string
    let remainingString = emailString;
    emails.forEach(email => {
      remainingString = remainingString.replace(email, " ");
    });

    // Extract standalone numbers
    const numberPattern = /\b\d+\b/g;
    let numbers = remainingString.match(numberPattern) || [];

    // Remove extracted numbers to avoid duplicates
    numbers.forEach(number => {
      remainingString = remainingString.replace(number, " ");
    });

    // Extract standalone words (letters only)
    const wordPattern = /\b[a-zA-Z]+\b/g;
    let words = remainingString.match(wordPattern) || [];

    // Merge emails, numbers, and words, then remove duplicates
    let formattedList = Array.from(new Set([...emails, ...numbers, ...words]));

    // Format the output
    let formattedResult = formattedList.map(item => item + ";").join(" ");

    return formattedResult.endsWith(";") ? formattedResult + " " : formattedResult;
  };

  const handleChange = (e) => {
    const newValue = e.target.value;

    // Determine if the change is an addition or deletion
    // if (newValue?.length < content?.length) {
    if (newValue) {
      // Only update when the input length increases (i.e., user types)
      let parts = newValue.split(";").map(part => part.trim()).filter(part => part !== ""); // Remove empty parts
      let lastPart = parts.length > 0 ? parts[parts.length - 1] : ""; // Get the last valid input
      setLatestInput(lastPart);
    } else setLatestInput("");
    if (newValue?.length <= 0) setLatestInput("");

    // Always update content
    setContent(newValue);
    setMailNotExist(false);
    setListFull(false);
  };

  const removeLastPartAndAdd = (newmail) => {
    setContent(prevContent => {
      if (!prevContent.includes(";")) return formatEmails(newmail); // If no ";" exists, replace all content

      // Remove last part and add formatted newmail
      let updatedContent = prevContent.split(";").slice(0, -1).join(";").trim();

      return updatedContent ? formatEmails(updatedContent + "; " + newmail) : formatEmails(newmail);
    });

    setResultList([])
    setLatestInput(""); // Update latest input
  };

  const validateContentToFormat = (content) => {
    const trimmedContent = content.trimEnd();

    // 1. Kiểm tra định dạng (các email cách nhau bởi dấu chấm phẩy + khoảng trắng)
    const regex = /^([\w.-]+@[\w.-]+\.\w+; )*[\w.-]+@[\w.-]+\.\w+;\s?$/;
    if (!regex.test(trimmedContent)) {
      return false;
    }

    // 2. Cắt chuỗi thành mảng email
    const emails = trimmedContent
      .split(";")
      .map(e => e.trim())        // bỏ khoảng trắng
      .filter(e => e.length > 0); // bỏ phần trống

    // 3. Kiểm tra trùng lặp
    const emailSet = new Set(emails);
    return emailSet.size === emails.length;

  };

  const handleAddMembers = async (idTeam, memberList) => {
    setIsAdding(true);
    const members = memberList.map(mem => mem.mail)
    try {
      const response = await postAddMembers(idTeam, members);
      if (response.status === APIStatus.success) {
        setIsAdding(false);
        setIsSuccessful(true);
        setIsAdded(true);
      } else console.error(response.data);
    } catch (error) {
      console.error("Error posting data: ", error);
    } finally {
      setIsAdding(false);
    }
  }


  useEffect(() => {
    if (!latestInput) {
      setResultList([]); // Clear results if content is empty
      return;
    }

    const controller = new AbortController();
    const { signal } = controller;

    // Debounce: Wait 1000ms after user stops typing
    const timeoutId = setTimeout(() => {
      searchUsers(latestInput, signal); // Call searchUsers after debounce
    }, 1000);

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    } // Clear timeout if user types again
  }, [latestInput]); // Runs when content changes

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchBoxRef.current &&
        !searchBoxRef.current.contains(event.target)
      ) {
        setLatestInput("");
        setResultList([]);
        setIsSearching(false)
      }
    };

    // Attach both event listeners
    document.addEventListener("mousedown", handleClickOutside);

    // Cleanup both event listeners on component unmount
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  if (!isOpen) return null;
  return (
    <>
      <div className={`overlay-layer ${isOpen ? "active" : ""}`} >
        <div className="modal-block" onClick={(e) => e.stopPropagation()}>
          <div className="modal-block-content">
            <div className="popup-header">
              <label className="popup-title">Add members</label>
              <p>You can add <b>a member</b> by email or name. When adding <b>multiple members</b>, please use a list of emails (click <b>Format</b> first). Note: You can only add up to <b style={{ color: "var(--main-color)" }}>200 people</b> at a time.</p>
            </div>
            {(mailNotExist || listFull)
              && (
                <span className="error-message">{mailNotExist ? "Mails not exist!" : `List is full! ${numberOfEmails - limitAddMembers} emails left`}</span>
              )
            }
            <div className="field-add-member">
              <textarea
                className="member-input"
                placeholder="Type a name or email"
                value={content}
                onChange={(e) => {
                  handleChange(e);
                }}
              ></textarea>
              <div className="tool-buttons-container">
                {isExecuting ? (
                  <span className="inform-message">
                    <LuLoaderCircle /> Executing...
                  </span>
                ) : mailNotExist || listFull ? (
                  <span className="inform-message error">
                    {mailNotExist ? "Mails not exist!" : `List is full! ${numberOfEmails - limitAddMembers} emails left`}
                  </span>
                ) : null}
                <button
                  className="add-to-list-button"
                  onClick={() => {
                    setContent(prevContent => formatEmails(prevContent));
                    setLatestInput("");
                    // console.log("Validate content: ", validateContentToFormat(content));

                  }}><LuNotebook />Format</button>
                <button
                  className="add-to-list-button"
                  title={
                    !content ? "Please type something!" :
                      !validateContentToFormat(content) ? "Something's wrong with the emails. Please Format first!" :
                        memberList?.length >= limitAddMembers ?
                          "Maximum number of members reached!" : ""
                  }
                  disabled={!validateContentToFormat(content) || memberList?.length >= limitAddMembers}
                  onClick={() => handleAddToList(content)}
                ><LuCheck />Add to list</button>
              </div>
              <div
                ref={searchBoxRef}
                className={`search-result-box ${resultList?.length > 0 || isSearching ? "active" : ""}`}
              >
                {isSearching ? (
                  <>
                    <div className="loading-searching">
                      <ImSpinner9 className="spin-icon" />
                    </div>
                  </>
                ) : (
                  <>
                    {resultList?.length > 0 && resultList.map((user, index) => (
                      <button
                        key={index}
                        onClick={() => removeLastPartAndAdd(user.mail)}
                      >
                        {user.avatar ? (
                          <img className="member-ava" src={user.avatar} alt="Error" />
                        ) : (
                          <div
                            className="member-ava"
                            style={{
                              backgroundColor: stringToColor(user.displayName),
                              color: adjustTextColor(stringToColor(user.displayName))
                            }}
                          >{getInitials(user.displayName)}</div>
                        )}
                        <div className="information">
                          <p className="name">{user.displayName}</p>
                          <p className="email">{user.mail}</p>
                        </div>
                      </button>
                    ))}
                  </>
                )}
              </div>
            </div>

            {memberList?.length > 0 && (
              <>
                <span className="number-members">Number of members: &nbsp;
                  <p className={memberList?.length >= limitAddMembers ? 'max-mem' : ''}>{memberList.length}</p>/{numberOfEmails}
                </span>
                <div className="field-member">
                  {memberList.map((member, index) => (
                    <div
                      key={index}
                      className='member-item adjust-member-item active'
                      title={member.displayName}
                    >
                      <div className='member-header'>
                        {member.avatar ? (
                          <img className="member-ava" src={member.avatar} alt="Error" />
                        ) : (
                          <div
                            className='member-ava'
                            style={{
                              backgroundColor: stringToColor(member.displayName),
                              color: adjustTextColor(stringToColor(member.displayName))
                            }}
                          >{getInitials(member.displayName)}</div>
                        )}
                        <div className='member-name'>
                          <p>{member.displayName}</p>
                          <p className='member-mail'>{member.mail}</p>
                        </div>
                      </div>
                      <div className='member-info'>
                        <LuX
                          onClick={() => {
                            setMemberList(prev => prev.filter(user => user.userId !== member.userId))
                            setListFull(false);
                          }}
                        />
                      </div>
                    </div >
                  ))}
                </div>
              </>
            )}


            <div className="buttons-container ed1">
              <button
                className="cancel"
                onClick={() => {
                  if (isAdded) {
                    fetchTeamMembers();
                    setIsAdded(false);
                  }
                  onClose();
                  setContent("");
                  setLatestInput("");
                  setMemberList([]);
                  setListFull(false);
                  setMailNotExist(false);
                }}
              >Cancel</button>
              <button
                className="trigger"
                disabled={!(memberList?.length > 0)}
                onClick={() => handleAddMembers(idTeamSelected, memberList)}
              >Add to team</button>
            </div>
          </div>
        </div>
      </div>

      {(isExecuting || isAdding) && (
        <div
          className="executing-overlay"
          style={{ backgroundColor: isAdding ? "rgba(121, 121, 121, 0.2)" : "" }}
        >
          <span className={`${isExecuting ? "inform-message" : "inform-creating"}`}>
            <LuLoaderCircle /> Executing...
          </span>
        </div>
      )}

      <DiagConfirmation
        type={"successfully added"}
        isOpen={isSuccessful}
        onClose={() => setIsSuccessful(false)}
        action1={() => {
          setMemberList([]);
          setListFull(false);
        }}
        action2={() => {
          setMemberList([]);
          setContent("");
          setLatestInput("");
          setMailNotExist(false);
          setListFull(false);
          setIsAdded(false);
          fetchTeamMembers();
          onClose();
        }}
      />
    </>
  )
}

export default AddMemberPopUp;